import { memo, useMemo } from 'react';
import {
  ChakraBox,
  ChakraDivider,
  ChakraFlex,
  ChakraSimpleGrid,
  ChakraTooltip,
  FAIconName,
  Icon,
  Text,
} from '@equip.health/ui';
import SkeletonLoaderList from '~/components/common/SkeletonLoaderList';
import FollowUpChips from './FollowUpChips';
import FollowUpSlot from './FollowUpSlot';
import FollowUpWindowLoader from './FollowUpWindowLoader';

import {
  WindowAvailabilityResult,
  windowKey,
} from '../hooks/useFollowUpAvailability';
import { getOverlappingOpenings, slotKey } from './followUp/overlap.util';

const TOP_LEVEL_ID = 'provider-assignment-follow-up-scheduling';

interface FollowUpSchedulingProps {
  isLoading: boolean;
  buckets: FollowUpWeekBucket[];
  series: FollowUpSeries[];
  selectedByWeek: Map<number, FollowUpOpening[]>;
  activeSeriesKey: string | null;
  windows: FollowUpWindow[];
  hostId?: string;
  appointmentType?: string;
  bufferMinutes?: number;
  patientId?: string;
  supportIds?: string[];
  onWindowAvailability: (key: string, result: WindowAvailabilityResult) => void;
  onWindowError: (error: unknown) => void;
  onBestMatchSelect: () => void;
  onSeriesSelect: (series: FollowUpSeries) => void;
  onSeriesClear: () => void;
  onSlotSelect: (weekIndex: number, opening: FollowUpOpening) => void;
}

const FollowUpScheduling = ({
  isLoading,
  buckets,
  series,
  selectedByWeek,
  activeSeriesKey,
  windows,
  hostId,
  appointmentType,
  bufferMinutes = 0,
  patientId,
  supportIds,
  onWindowAvailability,
  onWindowError,
  onBestMatchSelect,
  onSeriesSelect,
  onSeriesClear,
  onSlotSelect,
}: FollowUpSchedulingProps) => {
  const loaders =
    hostId && appointmentType
      ? windows.map((w) => {
          const key = windowKey(w);
          return (
            <FollowUpWindowLoader
              appointmentType={appointmentType}
              hostId={hostId}
              key={key}
              onAvailability={onWindowAvailability}
              onError={onWindowError}
              patientId={patientId}
              supportIds={supportIds}
              window={w}
              windowKey={key}
            />
          );
        })
      : null;

  const disabledSlotKeys = useMemo(() => {
    const allDisabled = new Set<string>();
    for (const bucket of buckets) {
      const selected = selectedByWeek.get(bucket.weekIndex) ?? [];
      if (!selected.length) continue;
      const weekDisabled = getOverlappingOpenings(selected, bucket.openings, bufferMinutes);
      for (const key of weekDisabled) allDisabled.add(key);
    }
    return allDisabled;
  }, [buckets, bufferMinutes, selectedByWeek]);

  if (isLoading) {
    return (
      <ChakraFlex direction="column" gap="24px">
        {loaders}
        <SkeletonLoaderList
          id={`${TOP_LEVEL_ID}__placeholder`}
          itemHeight="64px"
          itemWidth="240px"
        />
      </ChakraFlex>
    );
  }

  const renderWeekBody = (
    bucket: FollowUpWeekBucket,
    selected: FollowUpOpening[] | undefined,
  ) => {
    const { weekIndex, openings } = bucket;
    if (!openings.length) {
      return (
        <ChakraFlex
          alignItems="center"
          height="100%"
          justifyContent="flex-start"
        >
          <Text
            color="neutral.secondary"
            id={`${TOP_LEVEL_ID}__week-${weekIndex}-none`}
            variant="body"
          >
            No availability
          </Text>
        </ChakraFlex>
      );
    }
    return (
      <ChakraSimpleGrid columns={3} spacing="12px">
        {openings.map((opening) => (
          <FollowUpSlot
            id={`${TOP_LEVEL_ID}__week-${weekIndex}`}
            isDisabled={disabledSlotKeys.has(slotKey(opening))}
            isSelected={
              selected?.some(
                (s) =>
                  s.startTime === opening.startTime &&
                  s.providerId === opening.providerId,
              ) ?? false
            }
            key={opening.startTime}
            onSelect={(o) => onSlotSelect(weekIndex, o)}
            opening={opening}
          />
        ))}
      </ChakraSimpleGrid>
    );
  };

  const hasOpenings = buckets.some((b) => b.openings.length > 0);

  if (!hasOpenings) {
    return (
      <ChakraFlex direction="column" gap="24px">
        {loaders}
        <ChakraFlex
          alignItems="center"
          color="neutral.secondary"
          height="100%"
          justifyContent="center"
        >
          <Text id={`${TOP_LEVEL_ID}__empty`} variant="body">
            No follow-up availability for this provider. You can skip this step.
          </Text>
        </ChakraFlex>
      </ChakraFlex>
    );
  }

  return (
    <ChakraFlex direction="column" gap="24px">
      {loaders}
      <FollowUpChips
        activeSeriesKey={activeSeriesKey}
        id={TOP_LEVEL_ID}
        onBestMatchSelect={onBestMatchSelect}
        onClear={onSeriesClear}
        onSelect={onSeriesSelect}
        series={series}
      />
      <ChakraFlex direction="column">
        {buckets.map((bucket, idx) => {
          const { weekIndex } = bucket;
          const selected = selectedByWeek.get(weekIndex);
          return (
            <ChakraFlex direction="column" key={weekIndex}>
              {idx > 0 ? (
                <ChakraDivider
                  borderColor="neutral.line"
                  id={`${TOP_LEVEL_ID}__week-${weekIndex}-divider`}
                  marginY="24px"
                />
              ) : null}
              <ChakraFlex alignItems="flex-start" gap="12px">
                <ChakraFlex direction="column" flexShrink={0} gap="6px" width="135px">
                  <Text
                    color="neutral.primary"
                    id={`${TOP_LEVEL_ID}__week-${weekIndex}-label`}
                    variant="h5"
                  >
                    {bucket.weekLabel}
                  </Text>
                  <Text
                    color="neutral.secondary"
                    id={`${TOP_LEVEL_ID}__week-${weekIndex}-range`}
                    variant="caption"
                  >
                    {bucket.rangeLabel}
                  </Text>
                  {weekIndex === 0 && (
                    <ChakraTooltip
                      backgroundColor="white"
                      borderRadius="6px"
                      boxShadow="0px 0px 8px 0px rgba(0, 0, 0, 0.16)"
                      label={
                        <Text
                          color="neutral.secondary"
                          id={`${TOP_LEVEL_ID}__week-${weekIndex}-tooltip-text`}
                          variant="caption"
                        >
                          No time slots shown before intake session
                        </Text>
                      }
                      maxWidth="180px"
                      offset={[26, -40]}
                      padding="18px 24px"
                      placement="right-start"
                    >
                      <ChakraFlex
                        alignItems="center"
                        color="primary.100"
                        cursor="default"
                        gap="4px"
                        sx={{ '& p': { color: 'var(--chakra-colors-primary-100)' } }}
                        width="fit-content"
                      >
                        <Icon color="primary.100" name={FAIconName.infoCircleRegular} />
                        <Text
                          id={`${TOP_LEVEL_ID}__week-${weekIndex}-intake-label`}
                          variant="captionMedium"
                        >
                          Intake week
                        </Text>
                      </ChakraFlex>
                    </ChakraTooltip>
                  )}
                </ChakraFlex>
                <ChakraBox alignSelf="stretch" flex="1">
                  {renderWeekBody(bucket, selected)}
                </ChakraBox>
              </ChakraFlex>
            </ChakraFlex>
          );
        })}
      </ChakraFlex>
    </ChakraFlex>
  );
};

export default memo(FollowUpScheduling);
